import React, { useState, useEffect } from 'react';
import { 
  Mic, Video, FileText, Radio, Tv, Globe, Plus, Search, Filter,
  ChevronRight, X, Loader2, Calendar, User, ExternalLink, Clock,
  TrendingUp, Eye, DollarSign, CheckCircle, AlertCircle
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const MEDIA_TYPES = [
  { id: 'interview', label: 'Interview', icon: Mic, color: 'bg-blue-500' },
  { id: 'podcast', label: 'Podcast', icon: Radio, color: 'bg-purple-500' },
  { id: 'article', label: 'Article', icon: FileText, color: 'bg-green-500' },
  { id: 'feature', label: 'Feature', icon: Globe, color: 'bg-cyan-500' },
  { id: 'press_release', label: 'Press Release', icon: FileText, color: 'bg-orange-500' },
  { id: 'social_mention', label: 'Social Mention', icon: Globe, color: 'bg-pink-500' }
];

const OUTLET_TYPES = [
  { id: 'digital', label: 'Digital', icon: Globe },
  { id: 'print', label: 'Print', icon: FileText },
  { id: 'tv', label: 'TV', icon: Tv },
  { id: 'radio', label: 'Radio', icon: Radio },
  { id: 'podcast', label: 'Podcast', icon: Mic }
];

const STATUS_CONFIG = {
  identified: { label: 'Identified', color: 'bg-gray-500' },
  pitched: { label: 'Pitched', color: 'bg-blue-500' },
  confirmed: { label: 'Confirmed', color: 'bg-green-500' },
  completed: { label: 'Completed', color: 'bg-emerald-500' },
  declined: { label: 'Declined', color: 'bg-red-500' }
};

const AEMedia = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [mediaOpps, setMediaOpps] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (userEmail) {
      checkRole();
      fetchMedia();
    }
  }, [userEmail, filterStatus, filterType]);

  const checkRole = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/user-role?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      setIsAdmin(data.role === 'admin');
      if (data.role === 'admin') {
        fetchStats();
      }
    } catch (err) {
      console.error('Error checking role:', err);
    }
  };

  const fetchMedia = async () => {
    try {
      let url = `${API}/api/always-elevate/media?user_email=${encodeURIComponent(userEmail)}`;
      if (filterStatus !== 'all') {
        url += `&status=${filterStatus}`;
      }
      if (filterType !== 'all') {
        url += `&media_type=${filterType}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        setMediaOpps(data.data);
      }
    } catch (err) {
      console.error('Error fetching media:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/media/stats?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const filteredMedia = mediaOpps.filter(m => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return m.title?.toLowerCase().includes(search) ||
             m.outlet?.toLowerCase().includes(search) ||
             m.journalist_name?.toLowerCase().includes(search);
    }
    return true;
  });

  const getMediaIcon = (mediaType) => {
    const type = MEDIA_TYPES.find(t => t.id === mediaType);
    return type?.icon || Globe;
  };

  const getMediaColor = (mediaType) => {
    const type = MEDIA_TYPES.find(t => t.id === mediaType);
    return type?.color || 'bg-gray-500';
  };

  const formatNumber = (num) => {
    if (!num) return '-';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ae-media-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">
                {isAdmin ? 'ELLE MAXWELL AGENCY' : 'ALWAYS ELEVATE'}
              </p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }} data-testid="ae-media-title">
                {isAdmin ? 'Media & Visibility CRM' : 'Media Coverage'}
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                {mediaOpps.length} media opportunities
              </p>
            </div>
            {isAdmin && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
                data-testid="create-media-btn"
              >
                <Plus className="w-4 h-4" />
                Add Opportunity
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8">
        {/* Stats Row (Admin Only) */}
        {isAdmin && stats && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Globe className="w-5 h-5 text-[#C4A052]" />
              </div>
              <p className="text-2xl font-bold">{stats.total_opportunities}</p>
              <p className="text-xs text-gray-400">Total Opportunities</p>
            </div>
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Eye className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-2xl font-bold">{formatNumber(stats.total_reach)}</p>
              <p className="text-xs text-gray-400">Total Reach</p>
            </div>
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-2xl font-bold">{formatCurrency(stats.total_value)}</p>
              <p className="text-xs text-gray-400">Media Value</p>
            </div>
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <p className="text-2xl font-bold">
                {stats.by_status?.find(s => s._id === 'confirmed')?.count || 0}
              </p>
              <p className="text-xs text-gray-400">Confirmed</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search media..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-black border border-white/20 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#C4A052]"
              data-testid="media-search"
            />
          </div>
          
          {/* Status Filters */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                filterStatus === 'all' ? 'bg-[#C4A052] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              All
            </button>
            {Object.entries(STATUS_CONFIG).slice(0, 4).map(([key, config]) => (
              <button
                key={key}
                onClick={() => setFilterStatus(filterStatus === key ? 'all' : key)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  filterStatus === key ? `${config.color} text-white` : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {config.label}
              </button>
            ))}
          </div>
        </div>

        {/* Type Filter Pills */}
        <div className="flex items-center gap-2 mb-6">
          <span className="text-sm text-gray-400">Type:</span>
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${
              filterType === 'all' ? 'bg-white/20 text-white' : 'bg-white/5 text-gray-400'
            }`}
          >
            All
          </button>
          {MEDIA_TYPES.map(type => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => setFilterType(filterType === type.id ? 'all' : type.id)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs transition-colors ${
                  filterType === type.id ? `${type.color} text-white` : 'bg-white/5 text-gray-400'
                }`}
              >
                <Icon className="w-3 h-3" />
                {type.label}
              </button>
            );
          })}
        </div>

        {/* Media List */}
        {filteredMedia.length === 0 ? (
          <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
            <Globe className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">No Media Opportunities</h3>
            <p className="text-gray-500 text-sm">
              {isAdmin ? 'Add your first media opportunity' : 'No upcoming media appearances'}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="media-list">
            {filteredMedia.map(media => {
              const MediaIcon = getMediaIcon(media.media_type);
              const statusConfig = STATUS_CONFIG[media.status] || STATUS_CONFIG.identified;
              
              return (
                <div
                  key={media.id}
                  onClick={() => setSelectedMedia(media)}
                  className="bg-black border border-white/20 rounded-xl p-5 cursor-pointer transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl ${getMediaColor(media.media_type)} flex items-center justify-center flex-shrink-0`}>
                        <MediaIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-lg">{media.title}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color} text-white`}>
                            {statusConfig.label}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm mt-1">{media.outlet}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          {media.journalist_name && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {media.journalist_name}
                            </span>
                          )}
                          {media.scheduled_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(media.scheduled_date).toLocaleDateString()}
                            </span>
                          )}
                          {media.reach && (
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {formatNumber(media.reach)} reach
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Detail Modal */}
      {selectedMedia && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl ${getMediaColor(selectedMedia.media_type)} flex items-center justify-center`}>
                  {(() => {
                    const MediaIcon = getMediaIcon(selectedMedia.media_type);
                    return <MediaIcon className="w-6 h-6 text-white" />;
                  })()}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{selectedMedia.title}</h3>
                  <p className="text-gray-400 text-sm">{selectedMedia.outlet}</p>
                </div>
              </div>
              <button onClick={() => setSelectedMedia(null)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status */}
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_CONFIG[selectedMedia.status]?.color || 'bg-gray-500'} text-white`}>
                  {STATUS_CONFIG[selectedMedia.status]?.label || selectedMedia.status}
                </span>
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm capitalize">{selectedMedia.media_type?.replace('_', ' ')}</span>
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm capitalize">{selectedMedia.outlet_type}</span>
              </div>

              {/* Description */}
              {selectedMedia.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Description</h4>
                  <p className="text-gray-300">{selectedMedia.description}</p>
                </div>
              )}

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                {selectedMedia.scheduled_date && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-1">Scheduled Date</h4>
                    <p>{new Date(selectedMedia.scheduled_date).toLocaleDateString()}</p>
                  </div>
                )}
                {selectedMedia.journalist_name && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-1">Contact</h4>
                    <p>{selectedMedia.journalist_name}</p>
                    {selectedMedia.journalist_email && (
                      <p className="text-sm text-gray-500">{selectedMedia.journalist_email}</p>
                    )}
                  </div>
                )}
                {selectedMedia.reach && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-1">Est. Reach</h4>
                    <p className="text-blue-400">{formatNumber(selectedMedia.reach)}</p>
                  </div>
                )}
                {selectedMedia.value && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-1">Media Value</h4>
                    <p className="text-[#C4A052]">{formatCurrency(selectedMedia.value)}</p>
                  </div>
                )}
              </div>

              {/* Talking Points */}
              {selectedMedia.talking_points && selectedMedia.talking_points.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Talking Points</h4>
                  <ul className="space-y-1">
                    {selectedMedia.talking_points.map((point, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                        <CheckCircle className="w-4 h-4 text-[#C4A052]" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Notes */}
              {selectedMedia.notes && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Notes</h4>
                  <p className="text-gray-300 text-sm">{selectedMedia.notes}</p>
                </div>
              )}

              {/* Link */}
              {selectedMedia.link && (
                <a
                  href={selectedMedia.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[#C4A052] hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Coverage
                </a>
              )}
            </div>

            <div className="p-6 border-t border-white/10 flex justify-end">
              <button
                onClick={() => setSelectedMedia(null)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal (Admin) */}
      {showCreateModal && isAdmin && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">Add Media Opportunity</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-400 text-sm">Media opportunity creation form coming soon.</p>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AEMedia;
